import React from "react";
import { Link } from "react-router-dom";
import { MdErrorOutline, MdWarningAmber, MdInfoOutline, MdChevronRight } from "react-icons/md";
import { DashboardAlert, AlertSeverity } from "../../../../interfaces/Dashboard";

/**
 * Faixa de alertas acionáveis.
 *
 * ⚠ **Retorna `null` quando não há nada a fazer.** Alerta que aparece sempre — inclusive zerado —
 * é ruído, e ruído treina o gestor a ignorar a faixa inteira. O backend só emite item com
 * `count > 0`; aqui a regra é não desenhar um contêiner vazio.
 *
 * Cada item é um `<Link>` para o `href` que o **backend** decidiu: quem sabe por que o alerta
 * existe sabe para onde ele leva.
 */

const STYLES: Record<AlertSeverity, { icon: JSX.Element; box: string; chip: string }> = {
  danger: {
    icon: <MdErrorOutline className="h-5 w-5" aria-hidden="true" />,
    box: "border-red-200 bg-red-50 text-red-800 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/30",
    chip: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
  warning: {
    icon: <MdWarningAmber className="h-5 w-5" aria-hidden="true" />,
    box: "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/30",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  info: {
    icon: <MdInfoOutline className="h-5 w-5" aria-hidden="true" />,
    box: "border-dv-blue-200 bg-dv-blue-500/5 text-dv-blue-700 hover:bg-dv-blue-500/10 dark:border-dv-cyan-500/30 dark:bg-dv-cyan-500/10 dark:text-dv-cyan-300 dark:hover:bg-dv-cyan-500/20",
    chip: "bg-dv-blue-500/10 text-dv-blue-700 dark:bg-dv-cyan-500/20 dark:text-dv-cyan-300",
  },
};

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const AlertStrip: React.FC<{ alerts: DashboardAlert[] }> = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;

  return (
    <section aria-label="Alertas" className="flex flex-col gap-2">
      {alerts.map((alert) => {
        const s = STYLES[alert.severity] ?? STYLES.info;
        return (
          <Link
            key={alert.code}
            to={alert.href}
            className={`group flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm transition-colors ${s.box}`}
          >
            <span className="mt-0.5 shrink-0">{s.icon}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold">{alert.title}</p>
                {alert.amount != null && alert.amount > 0 && (
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${s.chip}`}>
                    {fmtMoney(alert.amount)}
                  </span>
                )}
              </div>
              <p className="text-xs opacity-90">{alert.description}</p>
            </div>
            <MdChevronRight
              className="mt-0.5 h-5 w-5 shrink-0 opacity-50 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
        );
      })}
    </section>
  );
};

export default AlertStrip;
